import React, { useEffect, useState } from 'react';
import { db, auth } from './firebaseConfig';
import { 
  collection, query, orderBy, onSnapshot, doc, 
  updateDoc, deleteDoc, addDoc,setDoc, serverTimestamp 
} from 'firebase/firestore';
import { onAuthStateChanged, signOut, sendPasswordResetEmail, createUserWithEmailAndPassword } from 'firebase/auth';
import { 
  PhoneCall, Eye, Navigation, Trash2, X, MessageSquare, 
  LogOut, Menu, Search, Activity, FileText, MessageCircle,
  ChevronLeft, ChevronRight, Download, CheckCircle, Clock, AlertTriangle, 
  Cpu, HardDrive, Terminal, Mail, UserPlus, ShieldCheck
} from 'lucide-react';
import logo from './logo.png'; 
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


const exportToPDF = (incident) => {
  const doc = new jsPDF();
  
  // 🎨 Branding Header
  doc.setFillColor(6, 78, 59); 
  doc.rect(0, 0, 210, 40, 'F');
  
  doc.setTextColor(251, 191, 36); 
  doc.setFontSize(22);
  doc.text("SAFEGUARD NG: INCIDENT REPORT", 15, 25);
  
  // 👤 REPORTER DETAILS (Using the standalone autoTable function)
  autoTable(doc, {
    startY: 50,
    head: [['FIELD', 'VERIFIED DATA']],
    body: [
      ["REPORTER NAME", incident.name || incident.reporterName || "N/A"],
      ["NIN NUMBER", incident.reporterNIN || "N/A"],
      ["EMAIL", incident.email || "N/A"],
      ["LOCATION", incident.location || "N/A"],
      ["STATUS", incident.status || "Pending"]
    ],
    headStyles: { fillColor: [6, 78, 59], textColor: [251, 191, 36] },
    styles: { font: "helvetica", fontSize: 10 }
  });

  // 🚩 INCIDENT DESCRIPTION
  const finalY = doc.lastAutoTable.finalY; // Gets the position after the table
  doc.setTextColor(0);
  doc.setFontSize(14);
  doc.text("DESCRIPTION / EVIDENCE SUMMARY", 15, finalY + 15);
  
  doc.setFontSize(11);
  const splitDesc = doc.splitTextToSize(incident.description || "No description provided.", 180);
  doc.text(splitDesc, 15, finalY + 25);

  // 🏁 Save the file
  doc.save(`SafeGuard_Report_${incident.id}.pdf`);
};
const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('incidents'); 
  const [incidents, setIncidents] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false); 
  const [currentPage, setCurrentPage] = useState(1);
  const [currentAuthUser, setCurrentAuthUser] = useState(null);

  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPass, setNewAdminPass] = useState('');
  const [confirmAdminPass, setConfirmAdminPass] = useState(''); // ⬅️ ADD THIS LINE HERE
  const itemsPerPage = 6;

  const isSuperAdmin = (email) => {
    const superAdmins = ['vickhuston@gmail.com', 'support@nathconsult.com'];
    return superAdmins.includes(email?.toLowerCase());
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentAuthUser(user.email.toLowerCase());
      } else {
        window.location.href = '/'; 
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    signOut(auth).catch((error) => console.error("Logout Error:", error));
  };

const handleCreateAdmin = async (e) => {
  e.preventDefault();
  if (!isSuperAdmin(currentAuthUser)) return;

  // 🛡️ SECURITY VALIDATION
  const hasMinLength = newAdminPass.length >= 6;
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newAdminPass);
  const passwordsMatch = newAdminPass === confirmAdminPass;

  if (!hasMinLength || !hasSpecialChar || !passwordsMatch) {
    alert("Security requirements not met.");
    return;
  }

  try {
    // 1. Create the user in Firebase Auth
    await createUserWithEmailAndPassword(auth, newAdminEmail, newAdminPass);
    
    // 2. Save to Firestore using the EMAIL as the Document ID (fixes your login issue)
    const adminEmailLower = newAdminEmail.toLowerCase();
    await setDoc(doc(db, "admins", adminEmailLower), {
      email: adminEmailLower,
      role: 'admin',
      timestamp: serverTimestamp()
    });

    alert("New Admin Created Successfully");
    setNewAdminEmail('');
    setNewAdminPass('');
    setConfirmAdminPass('');
    setShowAddAdmin(false);
  } catch (err) {
    alert(err.message);
  }
};

  const handleResetPassword = (email) => {
    if (!email) return;
    sendPasswordResetEmail(auth, email)
      .then(() => alert(`Password reset link sent to ${email}`))
      .catch((err) => alert(err.message));
  };

  useEffect(() => {
    if (!db) return;
    const unsubInc = onSnapshot(query(collection(db, "incidents"), orderBy("timestamp", "desc")), (s) => 
      setIncidents(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubFed = onSnapshot(query(collection(db, "feedback"), orderBy("timestamp", "desc")), (s) => 
      setFeedbacks(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubAdm = onSnapshot(query(collection(db, "admins"), orderBy("timestamp", "desc")), (s) => 
      setAdmins(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    
    return () => { unsubInc(); unsubFed(); unsubAdm(); };
  }, []);

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await updateDoc(doc(db, activeTab, id), { status: newStatus });
      if (selectedIncident?.id === id) setSelectedIncident({ ...selectedIncident, status: newStatus });
    } catch (err) { console.error("Update failed:", err); }
  };

  const getRawData = () => {
    if (activeTab === 'feedback') return feedbacks;
    if (activeTab === 'admins') return admins;
    return incidents;
  };

  const reportsToday = incidents.filter(i => {
    if (!i.timestamp) return false;
    try {
      return i.timestamp.toDate().toDateString() === new Date().toDateString();
    } catch (e) { return false; }
  }).length;

  const filteredData = getRawData().filter(item => {
    const s = searchQuery.toLowerCase();
    if (activeTab === 'incidents') {
      return (item.reporterNIN || "").toLowerCase().includes(s);
    }
    const name = (item.reporterName || item.name || item.fullName || item.subject || "").toLowerCase();
    return name.includes(s);
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const isVideo = (url) => url?.match(/\.(mp4|webm|ogg|mov|m4v)/i) || url?.includes('video');

  return (
    <div className="admin-container">
      <header className="main-header">
        <div className="header-inner">
          <div className="h-left-group">
            <img src={logo} alt="SafeGuard" className="master-logo" />
             <span className="brand-subtext">SafeGuard NG</span>

            <div className="v-divider hide-mobile"></div>
            <nav className="desktop-nav hide-mobile">

              <button className={activeTab === 'incidents' ? 'active' : ''} onClick={() => {setActiveTab('incidents'); setCurrentPage(1);}}>Reports</button>
              <button className={activeTab === 'feedback' ? 'active' : ''} onClick={() => {setActiveTab('feedback'); setCurrentPage(1);}}>Feedback</button>
              <button className={activeTab === 'admins' ? 'active' : ''} onClick={() => {setActiveTab('admins'); setCurrentPage(1);}}>Admins</button>
            </nav>
          </div>

          <div className="h-right-group">
            {activeTab === 'admins' && isSuperAdmin(currentAuthUser) && (
              <button className="icon-add-btn" onClick={() => setShowAddAdmin(!showAddAdmin)} title="Add New Admin">
                {showAddAdmin ? <X size={22} /> : <UserPlus size={22} />}
              </button>
            )}
            
            <span className="user-label hide-mobile">{currentAuthUser}</span>
            <div className="v-divider hide-mobile" style={{ margin: '0 15px' }}></div>
            <button className="icon-logout-btn" onClick={handleLogout} title="Logout">
              <LogOut size={22} />
            </button>
            <button className="mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
        
       {menuOpen && (
    <div className="mobile-overlay-nav animate-in">
      <button onClick={() => {setActiveTab('incidents'); setMenuOpen(false); setCurrentPage(1);}}>
        <FileText size={18} /> Reports
      </button>
      <button onClick={() => {setActiveTab('feedback'); setMenuOpen(false); setCurrentPage(1);}}>
        <MessageSquare size={18} /> Feedback
      </button>
      <button onClick={() => {setActiveTab('admins'); setMenuOpen(false); setCurrentPage(1);}}>
        <ShieldCheck size={18} /> Admins
      </button>
    </div>
  )}
      </header>

      <main className="content-body">
       {showAddAdmin && (
  <div className="admin-form-dropdown animate-in">
    <form onSubmit={handleCreateAdmin}>
      <div className="form-title"><ShieldCheck size={18}/> Add System Admin</div>
      <div className="form-row" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <div style={{ display: 'flex', gap: '15px' }}>
           <input type="email" placeholder="Admin Email" value={newAdminEmail} onChange={(e)=>setNewAdminEmail(e.target.value)} required />
           <input type="password" placeholder="New Password" value={newAdminPass} onChange={(e)=>setNewAdminPass(e.target.value)} required />
           <input type="password" placeholder="Confirm Password" value={confirmAdminPass} onChange={(e)=>setConfirmAdminPass(e.target.value)} required />
        </div>

        {/* 🔐 LIVE VALIDATION UI */}
        <div className="pass-requirements" style={{ display: 'flex', gap: '20px', padding: '10px', background: '#f8fafc', borderRadius: '8px', fontSize: '12px' }}>
           <span style={{ color: newAdminPass.length >= 6 ? '#059669' : '#94a3b8', fontWeight: '700' }}>
             {newAdminPass.length >= 6 ? '✅' : '○'} 6+ Characters
           </span>
           <span style={{ color: /[!@#$%^&*(),.?":{}|<>]/.test(newAdminPass) ? '#059669' : '#94a3b8', fontWeight: '700' }}>
             {/[!@#$%^&*(),.?":{}|<>]/.test(newAdminPass) ? '✅' : '○'} Special Char (!@#$)
           </span>
           <span style={{ color: (newAdminPass === confirmAdminPass && confirmAdminPass !== "") ? '#059669' : '#94a3b8', fontWeight: '700' }}>
             {(newAdminPass === confirmAdminPass && confirmAdminPass !== "") ? '✅' : '○'} Passwords Match
           </span>
        </div>

        <button 
          type="submit" 
          className="submit-admin-btn"
          disabled={!(newAdminPass.length >= 6 && /[!@#$%^&*(),.?":{}|<>]/.test(newAdminPass) && newAdminPass === confirmAdminPass)}
          style={{ 
            opacity: (newAdminPass.length >= 6 && /[!@#$%^&*(),.?":{}|<>]/.test(newAdminPass) && newAdminPass === confirmAdminPass) ? 1 : 0.5,
            cursor: (newAdminPass.length >= 6 && /[!@#$%^&*(),.?":{}|<>]/.test(newAdminPass) && newAdminPass === confirmAdminPass) ? 'pointer' : 'not-allowed',
            height: '45px'
          }}
        >
          Create Account
        </button>
      </div>
    </form>
  </div>
)}




        <div className="search-wrap">
          <div className="search-input-container">
            <Search size={20} className="s-icon" />
            <input 
              type="text" 
              placeholder={activeTab === 'incidents' ? "Search by ID Number..." : `Search ${activeTab}...`} 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
            />
          </div>
        </div>

        <div className="stats-strip">
          <div className="stat-box"><FileText color="#4c1d95"/> <div><p>Total Reports</p><strong>{incidents.length}</strong></div></div>
          <div className="stat-box"><MessageCircle color="#059669"/> <div><p>Feedback</p><strong>{feedbacks.length}</strong></div></div>
          <div className="stat-box"><Activity color="#d97706"/> <div><p>Today's Report</p><strong>{reportsToday}</strong></div></div>
        </div>

        <div className="main-data-card">
          <div className="table-overflow">
            <div className="table-overflow">
  <table className="data-table">
    <thead>
      <tr>
        {/* The first column stays flexible */}
        <th>{activeTab === 'feedback' ? 'Technical User' : activeTab === 'admins' ? 'Admin Name' : 'Reporter'}</th>
        
        {/* This column hides on mobile to save space */}
        <th className="hide-mobile">{activeTab === 'feedback' ? 'Subject' : 'Emergency Report'}</th>
        
        <th>Status</th>
        
        {/* We set a minimum width here so the buttons never squash */}
        <th style={{ minWidth: '160px' }}>Admin Action Panel</th>
      </tr>
    </thead>
    <tbody>
      {paginatedData.map((item, idx) => (
        <tr key={item.id} className="table-row animate-in" style={{ animationDelay: `${idx * 0.05}s` }}>
          <td>
            <div className="profile-cell">
              <div 
                className="profile-thumb clickable-avatar" 
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedIncident({
                    ...item,
                    evidenceUrls: [item.profilePic || item.reporterProfilePic || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name || item.reporterName || "User")}&background=064e3b&color=fbbf24&bold=true`],
                    isProfileView: true 
                  });
                  setCurrentMediaIndex(0);
                }}
              >
                <img src={item.profilePic || item.reporterProfilePic || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name || item.reporterName || "User")}&background=064e3b&color=fbbf24&bold=true`} alt="Profile" />
              </div>
              <div className="profile-info">
                <div className="name-with-badge">
                  <strong>{item.name || item.reporterName || item.fullName}</strong>
                  <CheckCircle size={14} className="verified-icon" />
                </div>
                <small>{item.email || `ID: ${item.reporterNIN || 'N/A'}`}</small>
              </div>
            </div>
          </td>

          <td className="hide-mobile">
            {activeTab === 'feedback' ? (
               <div className="subject-tag">{item.subject || "Technical Issue"}</div>
            ) : (item.description || item.message || "").substring(0, 40) + "..."}
          </td>

          <td>
            <span className={`status-pill ${item.status?.toLowerCase() || (activeTab === 'admins' ? 'resolved' : 'pending')}`}>
              {item.status || (activeTab === 'admins' ? 'Admin' : 'Active')}
            </span>
          </td>

          <td className="actions-cell">
            {/* 📱 This box will now scroll horizontally if buttons overflow */}
            <div className="action-scroll-box">
              <button className="btn-act view" onClick={() => {setSelectedIncident(item); setCurrentMediaIndex(0);}}><Eye size={18}/></button>
              {activeTab !== 'admins' ? (
                <>
            {activeTab === 'incidents' && (
  <button 
    className="btn-act map-priority-icon" 
    onClick={() => {
      if (item.latitude && item.longitude) {
        // Direct Deep-Link for Google Maps Navigation
        const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${item.latitude},${item.longitude}&travelmode=driving`;
        window.open(navUrl, '_blank');
      } else {
        alert("GPS Coordinates missing.");
      }
    }}
    title="START LIVE NAVIGATION"
  >
    <Navigation size={18} />
    {/* The pulse dot is now a small overlay on the icon */}
    <div className="mini-pulse"></div>
  </button>
)}
                  <button className="btn-act call" onClick={() => window.open(`tel:${item.phone || item.reporterPhone}`)}><PhoneCall size={18}/></button>
                </>
              ) : (
                <button className="btn-act reset" onClick={() => handleResetPassword(item.email)}><Mail size={18}/></button>
              )}
              {(activeTab !== 'admins' || isSuperAdmin(currentAuthUser)) && (
                <button className="btn-act del" onClick={() => window.confirm("Delete record?") && deleteDoc(doc(db, activeTab, item.id))}><Trash2 size={18}/></button>
              )}
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>
          </div>
          <div className="pagination-bar">
            <div className="page-text">Page {currentPage} of {totalPages}</div>
            <div className="page-controls">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><ChevronLeft size={18}/></button>
              <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}><ChevronRight size={18}/></button>
            </div>
          </div>
        </div>





{/* 🏢 OFFICIAL CORPORATE ENTITY SNAPSHOT */}
{/* 🏢 OFFICIAL CORPORATE ENTITY SNAPSHOT - Now on Reports and Admins */}
{(activeTab === 'admins' || activeTab === 'incidents') && (
  <div className="company-info-card animate-in">
    <div className="company-card-header">
      <ShieldCheck size={24} color="#fbbf24" />
      <div>
        <h3>Official Entity Profile</h3>
        <p>Verified via Dun & Bradstreet (D-U-N-S)</p>
      </div>
    </div>
    
    <div className="company-details-grid">
      <div className="detail-item">
        <label>Legal Name</label>
        <strong>Nathconsult LLC</strong>
      </div>
      <div className="detail-item">
        <label>D-U-N-S Number</label>
        <strong className="duns-text">14-188-2601</strong>
      </div>
      <div className="detail-item">
        <label>Registered Address</label>
        <p>1209 Indianola Trl, Anna, TX, US</p>
      </div>
      <div className="detail-item">
        <label>Status</label>
        <span className="legal-status-tag">Active / Verified 2026</span>
      </div>
    </div>
  </div>
)}










       {/* 🛡️ NEW FOOTER ADDED HERE */}
          <footer className="dashboard-footer">
            <div className="footer-line"></div>
            <p>© 2026 <strong>SafeGuard NG</strong>. Managed by <strong>Nathconsult LLC</strong></p>
          </footer>
      


      </main>

      {selectedIncident && (
        <div className="m-overlay" onClick={() => setSelectedIncident(null)}>
          <div className="m-window full-view animate-in" onClick={e => e.stopPropagation()}>
            <div className="m-header-grad">
  <h3>{selectedIncident.isProfileView ? 'USER IDENTITY SNAPSHOT' : (activeTab === 'feedback' ? 'TECHNICAL TICKET' : 'EVIDENCE VIEW FOR EMERGENCY RESPONSE')}</h3>
  <button className="m-close" onClick={() => setSelectedIncident(null)}><X size={28}/></button>
</div>
            <div className="m-body-split">
              <div className="m-media-part">
                {selectedIncident.evidenceUrls?.length > 0 ? (
                  <div className="m-media-viewer">
                    {isVideo(selectedIncident.evidenceUrls[currentMediaIndex]) ? (
                      <video controls key={selectedIncident.evidenceUrls[currentMediaIndex]} className="m-media-el"><source src={selectedIncident.evidenceUrls[currentMediaIndex]} /></video>
                    ) : (
                      <img src={selectedIncident.evidenceUrls[currentMediaIndex]} className="m-media-el" alt="" />
                    )}
                    
                    {selectedIncident.evidenceUrls.length > 1 && (
                      <div className="m-nav-controls">
                        <button className="m-nav-btn l" onClick={() => setCurrentMediaIndex(p => (p - 1 + selectedIncident.evidenceUrls.length) % selectedIncident.evidenceUrls.length)}><ChevronLeft size={40}/></button>
                        <button className="m-nav-btn r" onClick={() => setCurrentMediaIndex(p => (p + 1) % selectedIncident.evidenceUrls.length)}><ChevronRight size={40}/></button>
                      </div>
                    )}
<button 
  className="m-download-btn" 
  onClick={(e) => {
    e.stopPropagation();
    const mediaUrl = selectedIncident.evidenceUrls[currentMediaIndex];
    
    // We append &download=1 to the URL - sometimes Firebase respects this
    const downloadUrl = mediaUrl.includes('?') 
      ? `${mediaUrl}&download=1` 
      : `${mediaUrl}?download=1`;

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.target = '_blank'; // Opens in new tab but tries to trigger download
    link.setAttribute('download', 'SafeGuard_Evidence.mp4');
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }}
>
  <Download size={16}/> Save Media
  
</button>              

    </div>
                ) : <div className="no-media-box" style={{color:'white'}}>No media attached</div>}
              </div>
              <div className="m-info-part">
                <div className="m-status-area">
                  <label>Update Status</label>

                   





                  <div className="m-status-grid">
                    <button className={`ms-btn p ${selectedIncident.status === 'Pending' ? 'on' : ''}`} onClick={() => handleStatusUpdate(selectedIncident.id, 'Pending')}>Pending</button>
                    <button className={`ms-btn i ${selectedIncident.status === 'Investigating' ? 'on' : ''}`} onClick={() => handleStatusUpdate(selectedIncident.id, 'Investigating')}>Investigating</button>
                    <button className={`ms-btn r ${selectedIncident.status === 'Resolved' ? 'on' : ''}`} onClick={() => handleStatusUpdate(selectedIncident.id, 'Resolved')}>Resolved</button>
                  </div>
                </div>
                <div className="m-details-scroll">
                  {activeTab === 'feedback' ? (
                    <div className="tech-details-box">
                      <div className="tech-row"><Cpu size={16}/> <strong>Device:</strong> {selectedIncident.device}</div>
                      <div className="tech-row"><HardDrive size={16}/> <strong>OS:</strong> {selectedIncident.osVersion}</div>
                      <div className="tech-row"><Terminal size={16}/> <strong>Subject:</strong> {selectedIncident.subject}</div>
                      <div className="tech-divider"></div>
                      <h5>Steps to Reproduce:</h5>
                      <p className="steps-text">{selectedIncident.steps || "No specific steps provided."}</p>
                    </div>
                  ) : (
                    <>
                      <p><strong> Reporter:</strong> {selectedIncident.reporterName}</p>
                      <p><strong> ID :</strong> {selectedIncident.reporterNIN}</p>
                      <p><strong> Address:</strong> {selectedIncident.reporterAddress}</p>
                      <div className="tech-divider"></div>
                      <h5>Incident Description:</h5>
                      <p className="steps-text">{selectedIncident.description}</p>
                    </>
                  )}
                  <div className="tech-divider"></div>
                  <p><strong>Contact:</strong> {selectedIncident.phone || selectedIncident.reporterPhone}</p>
                  <p><strong>Email:</strong> {selectedIncident.email || 'N/A'}</p>



   




                </div>
                <div className="m-actions-row">
  {/* Your existing status badge */}
  <span className={`status-badge ${selectedIncident.status?.toLowerCase()}`}>
    {selectedIncident.status || 'Pending'}
  </span>

  {/* The Export Button moved here */}
  <button 
    className="m-export-btn" 
    onClick={() => exportToPDF(selectedIncident)}
  > 
    <FileText size={11}/> Export report to PDF
  </button>
</div>

              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-in { animation: fadeIn 0.4s ease forwards; }

        .admin-container { background: #f4f7f6; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .main-header { background: linear-gradient(135deg, #2e1065 0%, #064e3b 100%); height: 90px; color: white; position: sticky; top: 0; z-index: 1000; border-bottom: 4px solid #fbbf24; }
        .header-inner { display: flex; align-items: center; justify-content: space-between; max-width: 1400px; margin: 0 auto; height: 100%; padding: 0 30px; }
        .h-left-group { display: flex; align-items: center; }
        .master-logo { height: 70px; width: auto; }
        .v-divider { width: 1px; height: 30px; background: rgba(255,255,255,0.2); margin: 0 25px; }
        .desktop-nav { display: flex; gap: 10px; }
        .desktop-nav button { background: none; border: none; color: #a7f3d0; font-weight: 700; cursor: pointer; padding: 10px 15px; border-radius: 8px; font-size: 15px; transition: 0.2s; }
        .desktop-nav button.active { background: rgba(255,255,255,0.15); color: white; }
        .h-right-group { display: flex; align-items: center; gap: 10px; }
        
        .admin-form-dropdown { background: white; border-radius: 12px; padding: 20px; margin-bottom: 25px; border: 1px solid #e2e8f0; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
        .form-title { font-weight: 700; color: #1e293b; display: flex; align-items: center; gap: 8px; margin-bottom: 15px; }
        .form-row { display: flex; gap: 15px; }
        .form-row input { flex: 1; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; }
        .submit-admin-btn { background: #064e3b; color: white; padding: 0 20px; border-radius: 8px; border: none; font-weight: 700; cursor: pointer; }

        .icon-add-btn { background: #fbbf24; border: none; color: #000; width: 42px; height: 42px; border-radius: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.3s; }
        .icon-add-btn:hover { transform: scale(1.05); filter: brightness(1.1); }
        .icon-logout-btn { background: #ef4444; border: none; color: white; width: 42px; height: 42px; border-radius: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.3s; }
        .icon-logout-btn:hover { background: #dc2626; transform: scale(1.05); }
        
        .mobile-menu-btn { display: none; background: none; border: none; color: white; cursor: pointer; }

        .content-body { max-width: 1400px; margin: 0 auto; padding: 30px; }
        .search-input-container { background: white; border-radius: 12px; display: flex; align-items: center; padding: 0 20px; border: 1px solid #e2e8f0; }
        .search-input-container input { flex: 1; border: none; padding: 18px 10px; outline: none; font-size: 15px; }
        .stats-strip { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 25px 0; }
        .stat-box { background: white; padding: 20px; border-radius: 16px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 15px; }
        .stat-box strong { font-size: 24px; color: #1e293b; display: block; }
        .stat-box p { font-size: 12px; color: #64748b; text-transform: uppercase; font-weight: 700; margin: 0; }
        
        .main-data-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden; }
        .data-table { width: 100%; border-collapse: collapse; }
        .data-table th { background: #f8fafc; padding: 15px 20px; text-align: left; font-size: 12px; color: #64748b; text-transform: uppercase; border-bottom: 1px solid #e2e8f0; }
        .data-table td { padding: 12px 20px; border-bottom: 1px solid #f1f5f9; }
        
        /* FIXED: Profile Image Sizing */
        .profile-thumb { 
  width: 48px; 
  height: 48px; 
  border-radius: 50%; /* ⬅️ Perfect Circle */
  overflow: hidden; 
  flex-shrink: 0; 
  background: #064e3b; 
  border: 2px solid #fbbf24; /* Gold ring around the avatar */
  box-shadow: 0 4px 10px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.profile-thumb:hover {
  transform: scale(1.15) rotate(5deg); /* Slight pop and tilt */
  box-shadow: 0 0 15px rgba(251, 191, 36, 0.4);
}

.profile-thumb img { 
  width: 100%; 
  height: 100%; 
  object-fit: cover; 
}
        .status-pill { padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
        .status-pill.pending { background: #fffbeb; color: #92400e; }
        .status-pill.investigating { background: #fef2f2; color: #b91c1c; }
        .status-pill.resolved { background: #f0fdf4; color: #15803d; }
        
        .actions-cell { min-width: 150px; }
        .action-scroll-box { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 4px; scrollbar-width: none; }
        
        /* RESTORED: Action Panel Hovers */
        .btn-act { border: none; padding: 8px; border-radius: 8px; cursor: pointer; background: #f1f5f9; color: #64748b; flex-shrink: 0; transition: all 0.2s; }
        .btn-act:hover { background: #e2e8f0; color: #1e293b; transform: translateY(-2px); }
        .btn-act.view:hover { background: #4c1d95; color: white; }
        .btn-act.del:hover { background: #ef4444; color: white; }
        .btn-act.map:hover { background: #059669; color: white; }
        .btn-act.call:hover { background: #fbbf24; color: #000; }
        
        .pagination-bar { padding: 15px 25px; display: flex; justify-content: space-between; align-items: center; background: #fafafa; border-top: 1px solid #f1f5f9; }
        
        /* FULL VIEW MODAL & NAVIGATION */
        .m-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.92); z-index: 2000; display: flex; align-items: center; justify-content: center; }
        .m-window.full-view { width: 100vw; height: 100vh; border-radius: 0; display: flex; flex-direction: column; }
        .m-header-grad { background: linear-gradient(135deg, #4c1d95, #064e3b); color: white; padding: 20px 30px; display: flex; justify-content: space-between; flex-shrink: 0; }
        .m-body-split { display: grid; grid-template-columns: 1.3fr 0.7fr; flex: 1; overflow: hidden; }
        .m-media-part { background: #000; position: relative; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .m-media-viewer { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; position: relative; }
        .m-media-el { max-width: 100%; max-height: 100%; object-fit: contain; }
        
        .m-nav-controls { position: absolute; inset: 0; pointer-events: none; display: flex; justify-content: space-between; align-items: center; padding: 0 20px; }
        .m-nav-btn { pointer-events: auto; background: rgba(255,255,255,0.1); color: white; border: none; width: 60px; height: 60px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.3s; }
        .m-nav-btn:hover { background: rgba(255,255,255,0.3); transform: scale(1.1); }

        .m-info-part { padding: 40px; overflow-y: auto; background: #fff; }
        .ms-btn { background: #f8fafc; border: 1px solid #e2e8f0; padding: 12px; border-radius: 10px; cursor: pointer; font-size: 11px; font-weight: 700; transition: 0.2s; }
        .ms-btn:hover { border-color: #4c1d95; }
        .ms-btn.on { background: #4c1d95; color: white; }
        .steps-text { background: #f8fafc; padding: 15px; border-radius: 12px; border: 1px solid #e2e8f0; font-size: 14px; line-height: 1.6; color: #475569; }
        .m-download-btn { position: absolute; bottom: 30px; right: 30px; background: #fbbf24; border: none; padding: 12px 25px; border-radius: 10px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px; z-index: 10; transition: 0.3s; }
        .m-download-btn:hover { transform: translateY(-3px); box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
        
       /* Overlay of mobile screen*/

      /* 📱 DARK GRADIENT MOBILE MENU */
.mobile-overlay-nav { 
  position: absolute; 
  top: 100px; 
  left: 0; 
  width: 100%; 
  /* 🎨 Deep Green to Royal Purple Gradient */
  background: linear-gradient(135deg, #064e3b 0%, #2e1065 100%); 
  display: flex; 
  flex-direction: column; 
  padding: 10px 0; 
  z-index: 1001; 
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  border-bottom: 4px solid #fbbf24;
  animation: slideDown 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.mobile-overlay-nav button { 
  background: transparent; 
  border: none; 
  color: #e2e8f0; 
  padding: 22px 30px; 
  text-align: left; 
  font-weight: 700; 
  font-size: 17px;
  display: flex; 
  align-items: center; 
  gap: 15px; 
  border-bottom: 1px solid rgba(255,255,255,0.05); 
  width: 100%; 
  cursor: pointer; 
  transition: all 0.3s ease;
  outline: none;
  opacity: 0; /* Starting state for animation */
}

/* ✨ MOUTH-WATERING HOVER (Gold/Yellow Glow) */
.mobile-overlay-nav button:hover {
  background: rgba(251, 191, 36, 0.15); /* Subtle Gold Tint */
  color: #fbbf24; /* SafeGuard Gold Text */
  padding-left: 45px; /* Deeper "push" effect */
  box-shadow: inset 4px 0 0 #fbbf24; /* Left edge gold accent */
}

/* Icon Color Change on Hover */
.mobile-overlay-nav button:hover svg {
  color: #fbbf24;
  transform: scale(1.2);
  transition: 0.3s;
}

/* 🎞️ STAGGERED ENTRANCE */
@keyframes slideDown {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.mobile-overlay-nav button:nth-child(1) { animation: slideIn 0.4s ease forwards 0.1s; }
.mobile-overlay-nav button:nth-child(2) { animation: slideIn 0.4s ease forwards 0.2s; }
.mobile-overlay-nav button:nth-child(3) { animation: slideIn 0.4s ease forwards 0.3s; }

@keyframes slideIn {
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
}

/* Hide divider on last item */
.mobile-overlay-nav button:last-child {
  border-bottom: none;
}
        /* Ensure the brand stack stays centered on mobile too */
        @media (max-width: 1024px) {
          .hide-mobile { display: none !important; }
          .mobile-menu-btn { display: block; }
          .header-inner { padding: 0 20px; }
          .master-logo { height: 50px; }
          .brand-subtext { font-size: 9px; }
        }



        /* 🛡️ FOOTER STYLING */
        .dashboard-footer {
          padding: 30px 20px;
          text-align: center;
          background: #fafafa;
        }
        .footer-line {
          height: 1px;
          background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
          margin-bottom: 20px;
          width: 80%;
          margin-inline: auto;
        }
        .dashboard-footer p {
          font-size: 13px;
          color: #94a3b8;
          letter-spacing: 0.5px;
          transition: 0.3s;
        }
        .dashboard-footer p strong {
          color: #475569;
          font-weight: 700;
        }
        .dashboard-footer:hover p {
          color: #064e3b;
          transform: translateY(-2px);
        }


       .brand-stack {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          transition: 0.3s ease;
        }
        .brand-stack:hover {
          transform: translateY(-2px);
        }
        .master-logo { 
          height: 65px; /* Slightly reduced height to accommodate text below */
          width: auto; 
          display: block;
        }
        .brand-subtext {
          font-size: 13px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: #fcf9f2; /* Gold color to match your border */
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          line-height: 1;
        }

        /* Adjusting header height slightly if needed for the stack */
        .main-header { 
          height: 100px; /* Increased from 90px to give the stack breathing room */
          background: linear-gradient(135deg, #2e1065 0%, #064e3b 100%); 
          color: white; 
          position: sticky; 
          top: 0; 
          z-index: 1000; 
          border-bottom: 4px solid #fbbf24; 
        }

        /* ... existing media queries ... */
        @media (max-width: 1024px) {
          .main-header { height: 100px; }
          /* ... other mobile overrides ... */
        }

       /* Profile Info ... */

       /* 👤 FIXED: Reporter Name & NIN Spacing */
/* 👤 UPDATED: Yellow Reporter Name */
.profile-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px; 
}

.profile-info strong {
  font-size: 14px;
  /* 🎨 SafeGuard Gold / Amber for high visibility */
  color: #d97706; 
  font-weight: 800; /* Extra bold to make the yellow pop */
  line-height: 1.2;
  display: block;
  text-transform: capitalize; /* Ensures names look professional */
  transition: 0.2s ease;
}

/* Profile name styled ... */
.name-with-badge {
  display: flex;
  align-items: center;
  gap: 6px; /* ⬅️ This keeps the badge from touching the name */
}

.verified-icon {
  color: #fbbf24; /* SafeGuard Gold */
  fill: rgba(251, 191, 36, 0.1); /* Subtle inner glow */
  flex-shrink: 0; /* Prevents the checkmark from being squashed */
}

/* Ensure the NIN line has that 4px gap we talked about */
.profile-info {
  display: flex;
  flex-direction: column;
  gap: 4px; 
}

/* ⚡ Hover Effect: Glow on mouse over */
.profile-cell:hover .profile-info strong {
  color: #fbbf24; /* Brightens to pure gold on hover */
  text-shadow: 0 0 8px rgba(251, 191, 36, 0.3);
  transform: translateX(2px);
}

/* profile picture view */
/* 👤 CLICKABLE AVATAR STYLING */
.clickable-avatar {
  cursor: pointer;
  position: relative;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 2px solid #fbbf24;
}

.clickable-avatar:hover {
  transform: scale(1.2) rotate(3deg);
  box-shadow: 0 0 20px rgba(251, 191, 36, 0.6);
  z-index: 10;
}

/* Subtle "Enlarge" icon appearance on hover */
.clickable-avatar::after {
  content: '+';
  position: absolute;
  inset: 0;
  background: rgba(6, 78, 59, 0.4); /* SafeGuard Green tint */
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  opacity: 0;
  transition: 0.2s;
  border-radius: 50%;
}

.clickable-avatar:hover::after {
  opacity: 1;
}

.profile-info small {
  font-size: 10px;
  color: #64748b; 
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase; /* Makes "NIN: XXXXX" look like a tag */
}

/* Optional: Add a tiny hover effect to the profile cell */
.profile-cell:hover .profile-info strong {
  color: #4c1d95; /* Turns purple on hover to match theme */
  transition: 0.2s;
}
/*Table scroll ... */
/* 1. Ensure the container allows horizontal scrolling */
.table-overflow {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch; /* Smooth swipe for iOS */
  border-radius: 12px;
}

/* 2. Set a minimum width for the table so it doesn't squash on small screens */
.data-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 550px; /* This forces the scroll when the screen is smaller than 550px */
}

/* 3. The swipeable Action Panel container */
.action-scroll-box {
  display: flex;
  gap: 8px;
  overflow-x: auto; /* Allows sliding the buttons */
  white-space: nowrap;
  padding: 4px 0;
  scrollbar-width: none; /* Hides scrollbar on Firefox */
}

.action-scroll-box::-webkit-scrollbar {
  display: none; /* Hides scrollbar on Chrome/Safari */
}

/* 4. Fix button size to ensure they stay round and clickable */
.btn-act {
  flex-shrink: 0; /* Important: prevents buttons from getting narrow */
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 5. Mobile Adjustments */
@media (max-width: 768px) {
  .content-body {
    padding: 15px; /* Tighter padding for mobile screens */
  }

  .hide-mobile {
    display: none !important; /* Strictly hide the extra columns */
  }

  .stats-strip {
    grid-template-columns: 1fr; /* Stack the total reports/feedback boxes */
  }

  .actions-cell {
    min-width: 160px; /* Reserves space for at least 3-4 buttons */
  }
}

/* Export pdf report */
.m-export-btn {
  background: #2e1065; /* Dark Purple */
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: 0.3s ease;
}

.m-export-btn:hover {
  background: #4c1d95; /* Lighter Purple */
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(46, 16, 101, 0.3);
}

/* Company Profile */




.company-info-card.footer-card {
  margin-top: 20px; /* Space between table and card */
  padding: 15px 25px;
  background: #0f172a; /* Solid dark professional background */
  border-left: 4px solid #fbbf24;
  border-radius: 12px;
}

.footer-grid {
  grid-template-columns: repeat(4, 1fr) !important; /* Spread items horizontally */
  gap: 15px;
}

@media (max-width: 900px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr !important; /* Stack 2x2 on tablets */
  }
}

@media (max-width: 600px) {
  .footer-grid {
    grid-template-columns: 1fr !important; /* Stack vertically on small phones */
  }
}


/* Tracker*/
/* Professional Dispatch Button Styling */
/* Clean Icon-Only Navigation Button */
.btn-act.map-priority-icon {
  position: relative;
  background: #064e3b !important; /* Dark Green */
  color: #fbbf24 !important; /* Gold Icon */
  border: 1px solid #fbbf24 !important;
  transition: all 0.2s ease;
}

.btn-act.map-priority-icon:hover {
  background: #059669 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(251, 191, 36, 0.3);
}

/* Small Pulse Dot on the corner of the icon */
.mini-pulse {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 6px;
  height: 6px;
  background: #fbbf24;
  border-radius: 50%;
  animation: miniNavPulse 1.5s infinite;
}

@keyframes miniNavPulse {
  0% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.9); }
  70% { box-shadow: 0 0 0 6px rgba(251, 191, 36, 0); }
  100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0); }
}

/* 🏢 COMPANY PROFILE CARD STYLES */
.company-info-card {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  padding: 25px;
  border-radius: 16px;
  margin-bottom: 25px;
  border-left: 5px solid #fbbf24;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
}

.company-card-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  padding-bottom: 15px;
}

.company-card-header h3 { margin: 0; font-size: 18px; letter-spacing: 0.5px; }
.company-card-header p { margin: 0; font-size: 12px; color: #94a3b8; }

.company-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.detail-item label {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  color: #fbbf24;
  font-weight: 700;
  margin-bottom: 5px;
}

.detail-item strong { font-size: 16px; color: #f8fafc; }
.detail-item p { margin: 0; font-size: 14px; color: #cbd5e1; }

.duns-text { font-family: 'Courier New', monospace; letter-spacing: 1px; }

.legal-status-tag {
  background: rgba(5, 150, 105, 0.2);
  color: #34d399;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid rgba(52, 211, 153, 0.3);
}

@media (max-width: 768px) {
  .company-details-grid { grid-template-columns: 1fr; gap: 15px; }
}

        @media (max-width: 1024px) {
          .hide-mobile { display: none !important; }
          .mobile-menu-btn { display: block; }
          .stats-strip { grid-template-columns: 1fr; }
          .m-body-split { grid-template-columns: 1fr; overflow-y: auto; }
          .m-media-part { height: 400px; }
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;